import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import PageMeta from '@/components/feature/PageMeta';
import { BreadcrumbSchema, WebPageSchema } from '@/components/feature/SEOSchema';
import { categorySites } from '@/mocks/categories';

const categoryMeta: Record<string, { name: string; icon: string; desc: string; keywords: string }> = {
  movie: { name: '电影', icon: 'ri-film-line', desc: '精选高清电影在线看片网站，涵盖最新大片、经典老片、4K超清资源。', keywords: '电影看片网站,高清电影网站,在线看电影,免费电影网站' },
  series: { name: '剧集', icon: 'ri-tv-line', desc: '追剧必备！精选剧集在线看片网站，美剧、韩剧、日剧、国产剧一网打尽。', keywords: '剧集看片网站,追剧网站,美剧在线,韩剧在线' },
  variety: { name: '综艺', icon: 'ri-emotion-laugh-line', desc: '热门综艺节目在线看片网站推荐，卫视同步更新，往期精彩不错过。', keywords: '综艺看片网站,在线综艺,免费综艺网站,卫视综艺' },
  anime: { name: '动漫', icon: 'ri-ghost-line', desc: '二次元天堂！精选动漫在线看片网站，日漫、国漫、美漫高清无删减。', keywords: '动漫看片网站,在线动漫,日漫网站,国漫在线' },
  documentary: { name: '纪录片', icon: 'ri-earth-line', desc: '探索世界！精选纪录片在线看片网站，自然、历史、科学尽在其中。', keywords: '纪录片看片网站,在线纪录片,BBC纪录片,国家地理' },
};

const allCategorySlugs = ['movie', 'series', 'variety', 'anime', 'documentary'];

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const activeSlug = slug && categoryMeta[slug] ? slug : 'movie';
  const meta = categoryMeta[activeSlug];
  const sites = categorySites[activeSlug] || [];

  return (
    <>
      <PageMeta
        title={`${meta.name}看片网站推荐2025｜高清免费${meta.name}网站合集`}
        description={meta.desc}
        keywords={meta.keywords}
        canonicalUrl={`https://fjhcjykj.cn/category/${activeSlug}`}
        ogTitle={`${meta.name}看片网站推荐2025｜高清免费${meta.name}网站合集`}
        ogDescription={meta.desc}
      />
      <WebPageSchema
        title={`${meta.name}看片网站推荐2025`}
        description={meta.desc}
        url={`https://fjhcjykj.cn/category/${activeSlug}`}
      />
      <BreadcrumbSchema
        items={[
          { name: '首页', url: 'https://fjhcjykj.cn/' },
          { name: meta.name, url: `https://fjhcjykj.cn/category/${activeSlug}` },
        ]}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <section className="bg-background-100/60 py-16 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8" aria-label="面包屑导航">
                <Link to="/" className="hover:text-foreground-700 transition-colors">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-800 font-medium">{meta.name}</span>
              </nav>

              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-background-50 border border-background-200/70">
                  <i className={`${meta.icon} text-2xl text-primary-600`}></i>
                </div>
                <div>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                    {meta.name}看片网站推荐
                  </h1>
                  <p className="text-sm text-foreground-500 mt-1">{meta.desc}</p>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap" role="tablist" aria-label="分类切换">
                {allCategorySlugs.map((catSlug) => {
                  const cat = categoryMeta[catSlug];
                  const isActive = catSlug === activeSlug;
                  return (
                    <Link
                      key={catSlug}
                      to={`/category/${catSlug}`}
                      role="tab"
                      aria-selected={isActive}
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                        isActive
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-50 text-foreground-600 hover:text-foreground-900 border border-background-200/70 hover:border-primary-200'
                      }`}
                    >
                      <i className={`${cat.icon} text-xs`}></i>
                      {cat.name}
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="py-12 md:py-16">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {sites.map((site, index) => (
                  <article
                    key={site.id}
                    className="group bg-background-50 border border-background-200/70 rounded-xl p-5 hover:border-primary-200 transition-all duration-300"
                    data-product-shop
                  >
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 bg-background-100">
                        <img
                          src={site.logoUrl}
                          alt={site.name}
                          className="w-full h-full object-cover"
                          width="56"
                          height="56"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1 truncate">
                          {site.name}
                        </h3>
                        <div className="flex items-center gap-1.5">
                          <div className="flex items-center gap-0.5">
                            {[...Array(5)].map((_, i) => (
                              <i
                                key={i}
                                className={`text-xs ${
                                  i < Math.floor(site.rating)
                                    ? 'ri-star-fill text-secondary-500'
                                    : 'ri-star-line text-foreground-300'
                                }`}
                              ></i>
                            ))}
                          </div>
                          <span className="text-xs font-semibold text-foreground-700">{site.rating}</span>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-500 leading-relaxed mb-4 line-clamp-2">
                      {site.description}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      {site.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2.5 py-1 rounded-md bg-background-100 text-xs text-foreground-600 font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}